import { User } from '@dc-shared-web/hooks/api/user'
import { BasicTeam } from '@dc-shared-web/hooks/api/team'
import { cn } from '@dc-shared-web/lib'
import reactStringReplace from 'react-string-replace'
import { ALL_TEAMS_OPTION } from '@dc-shared-web/lib/constants'

export const crunchDateFilters = new Map<string, string>([
  ['Today', 'Today'],
  ['Yesterday', 'Yesterday'],
  ['Last 7 days', 'from 7 days ago to now'],
  ['Last 30 days', 'from 30 days ago to now'],
  ['Last 90 days', 'from 90 days ago to now'],
  ['Last 365 days', 'from 365 days ago to now'],
])

export const slugifyName = (text: string) => {
  return text
    ? `${text}`
        .trim()
        .toLowerCase()
        // Slugs should only include alphanumeric characters and dashes
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_-]+/g, '-')
    : ''
}

export const slugifyUrl = (name: string, id: number) =>
  `${encodeURIComponent(slugifyName(name))}-${id}`

export const getIdFromNameSlug = (slug: string | null) => {
  return slug?.split('-').slice(-1)[0] || null
}

export const getCookie = (name: string): string | undefined => {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift()
  }
}

export const escapeHTMLAndHighlightSearchTerm = (
  text: string,
  searchTerm: string,
  highlightClassName = 'tw-bg-warning-200'
) => {
  if (!searchTerm) return text

  let termsToHighlight
  if (searchTerm.includes(' ')) {
    termsToHighlight = searchTerm
      .split(/\s+/)
      .filter((term) => term.trim() !== '')
  } else {
    termsToHighlight = [searchTerm]
  }

  const sanitizedTerms = termsToHighlight.map((term) =>
    term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  )
  const pattern = new RegExp(`(${sanitizedTerms.join('|')})`, 'gi')

  return reactStringReplace(text, pattern, (match, i) => (
    <mark key={i} className={cn('tw-rounded-md', highlightClassName)}>
      {match}
    </mark>
  ))
}

export const getUserTeamOptions = (user: User | undefined): BasicTeam[] => {
  if (!user || !user.teams || user.teams.length <= 1) {
    return []
  }

  const basicTeams: BasicTeam[] = user.teams.map((team) => ({
    uuid: team.id,
    id: team.teamId,
    text: team.name,
    customer_id: team.customer_id,
    isAdmin: team.is_admin,
  }))

  return [ALL_TEAMS_OPTION, ...basicTeams]
}

export const getUserDefaultTeamName = (
  user: User | undefined
): string | undefined => {
  if (!user) {
    return
  }

  if (user.is_super_admin) {
    return 'All Teams'
  }

  return user.teams.find((t) => t.is_current)?.name
}

export const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value)
}

export const formatCurrencyNoCents = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}
